import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { BrandMarkLink } from "../components/BrandMarkLink";
import { PaperGrain } from "../components/PaperGrain";
import { STORY_IMAGE } from "../assets/images";
import { api } from "../lib/api";

/**
 * Shared two-column auth shell. /login and /signup use this with different
 * eyebrow / headline / subhead / footer-link copy.
 */
const ImagePanel = () => (
  <div
    data-testid="auth-image-panel"
    className="relative min-h-[40vh] lg:min-h-screen overflow-hidden bg-napoli-espresso"
  >
    <img
      src={STORY_IMAGE}
      alt=""
      aria-hidden
      className="absolute inset-0 h-full w-full object-cover"
      style={{ filter: "brightness(0.65) saturate(0.85)" }}
    />
    <div
      aria-hidden
      className="absolute inset-0 bg-gradient-to-br from-napoli-espresso/70 via-napoli-espresso/50 to-napoli-espresso/85"
    />
    <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 py-20 lg:py-0">
      <BrandMarkLink size="md" theme="dark" testId="auth-brand-link" ariaLabel="Napoli — home" />
      <p
        className="mt-10 font-flourish text-napoli-gold leading-none"
        style={{ fontSize: "clamp(40px, 5vw, 56px)" }}
      >
        Tutti a tavola.
      </p>
      <p className="mt-4 font-body italic text-napoli-cream/75 text-sm tracking-wider">
        Everyone at the table.
      </p>
    </div>
  </div>
);

const ToggleButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    aria-pressed={active}
    className={
      "flex-1 py-3 font-body text-[12px] tracking-[0.2em] uppercase transition-colors duration-200 " +
      (active
        ? "bg-napoli-oxblood text-napoli-cream border border-napoli-oxblood"
        : "bg-transparent text-napoli-oxblood border border-napoli-oxblood/40 hover:border-napoli-oxblood")
    }
  >
    {children}
  </button>
);

const ErrorBanner = ({ children }) =>
  children ? (
    <div
      data-testid="auth-error"
      className="mt-4 border-l-2 border-napoli-oxblood bg-napoli-oxblood/5 px-4 py-3 font-body text-[13px] text-napoli-oxblood-dark"
    >
      {children}
    </div>
  ) : null;

const ERROR_COPY = {
  invalid_email: "Please enter a valid email address.",
  invalid_phone: "Please enter a valid phone number.",
  rate_limited: "Too many attempts. Please wait a few minutes and try again.",
  email_unavailable: "Email service is unavailable right now. Try phone, or come back shortly.",
  sms_unavailable: "SMS service is unavailable right now. Try email instead.",
  invalid_body: "Something looked off with your input. Please try again.",
};

const friendly = (err) => {
  const code = err?.response?.data?.detail?.error || err?.response?.data?.error;
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  if (err?.response?.status === 429) return ERROR_COPY.rate_limited;
  return "Something went wrong. Please try again.";
};

const Countdown = ({ seconds, onDone }) => {
  const [n, setN] = useState(seconds);
  useEffect(() => {
    if (n <= 0) {
      onDone?.();
      return undefined;
    }
    const t = setTimeout(() => setN((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [n, onDone]);
  if (n <= 0) return null;
  return (
    <span className="font-body text-[12px] text-napoli-espresso-soft" data-testid="auth-countdown">
      {n}s
    </span>
  );
};

export const AuthShell = ({
  mode, // "login" | "signup"
  eyebrow,
  headline,
  subhead,
  footer,
  testIdPrefix,
}) => {
  const navigate = useNavigate();
  const [channel, setChannel] = useState("phone"); // 'phone' | 'email'
  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null); // { masked_identifier, type }
  const [err, setErr] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const [promoOptIn, setPromoOptIn] = useState(false);

  // Reset state on channel switch
  useEffect(() => {
    setIdentifier("");
    setErr(null);
  }, [channel]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null);
    const value = (identifier || "").trim();
    if (!value) return;

    // Client-side validation: catch partial phone numbers before they reach
    // the backend rate limiter or Twilio. Email regex is the backend's job.
    if (channel === "phone" && !isValidPhoneNumber(value)) {
      setErr("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/request-code", {
        identifier: value,
        type: channel,
        ...(mode === "signup" ? { promotional_emails_opt_in: promoOptIn } : {}),
      });
      // Stash opt-in in sessionStorage so VerifyPage can forward it on verify
      if (mode === "signup") {
        try {
          window.sessionStorage.setItem("napoli.signup.promo_opt_in", String(promoOptIn));
        } catch (_) { /* ignore */ }
      }
      setSent(data);
      setResendIn(60);
    } catch (e2) {
      setErr(friendly(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const buttonLabel = channel === "phone" ? "Send Code" : "Send Magic Link";

  const successCopy = useMemo(() => {
    if (!sent) return null;
    if (sent.type === "phone") {
      return {
        line1: "Check your messages.",
        line2: `We sent a 6-digit code to ${sent.masked_identifier}.`,
      };
    }
    return {
      line1: "Check your inbox.",
      line2: `We sent a sign-in link to ${sent.masked_identifier}.`,
    };
  }, [sent]);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <ImagePanel />

      <section
        data-testid={`${testIdPrefix}-form-panel`}
        className="relative bg-napoli-cream text-napoli-espresso flex items-center justify-center px-6 py-16 lg:p-16 overflow-hidden"
      >
        <PaperGrain />
        <div className="relative z-10 w-full max-w-[420px]">
          <p
            data-testid={`${testIdPrefix}-eyebrow`}
            className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood text-center"
          >
            {eyebrow}
          </p>
          <h1
            data-testid={`${testIdPrefix}-headline`}
            className="mt-4 font-display font-bold text-[32px] sm:text-[36px] leading-tight text-napoli-espresso text-center"
          >
            {headline}
          </h1>
          {subhead ? (
            <p className="mt-4 font-body italic text-[14px] text-napoli-espresso-soft text-center">
              {subhead}
            </p>
          ) : null}

          {sent ? (
            <div data-testid={`${testIdPrefix}-success`} className="mt-10 text-center">
              <p className="font-display font-bold text-[22px] text-napoli-espresso">
                {successCopy.line1}
              </p>
              <p className="mt-3 font-body text-[14px] text-napoli-espresso-soft">
                {successCopy.line2}
              </p>

              {sent.type === "phone" ? (
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-continue-verify`}
                  onClick={() =>
                    navigate(
                      `/verify?type=phone&identifier=${encodeURIComponent(identifier.trim())}`
                    )
                  }
                  className="mt-8 inline-flex items-center gap-2 font-body text-[13px] tracking-widest uppercase text-napoli-oxblood border-b border-napoli-oxblood pb-1 hover:text-napoli-oxblood-dark"
                >
                  Continue to verify <ArrowRight size={14} />
                </button>
              ) : null}

              <div className="mt-10 flex items-center justify-center gap-3 text-[12px] font-body text-napoli-espresso-soft">
                <span>Didn&rsquo;t get it?</span>
                {resendIn > 0 ? (
                  <Countdown seconds={resendIn} onDone={() => setResendIn(0)} />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSent(null);
                      setErr(null);
                    }}
                    data-testid={`${testIdPrefix}-resend`}
                    className="text-napoli-oxblood underline underline-offset-4 hover:text-napoli-oxblood-dark"
                  >
                    Try again
                  </button>
                )}
              </div>

              <div className="mt-4 flex items-center justify-center">
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-back-to-form`}
                  onClick={() => {
                    setSent(null);
                    setIdentifier("");
                    setErr(null);
                    setResendIn(0);
                  }}
                  className="font-body text-[12px] text-napoli-espresso-soft underline underline-offset-4 hover:text-napoli-oxblood transition-colors"
                >
                  {sent.type === "phone"
                    ? "Use a different number"
                    : "Use a different email"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                data-testid={`${testIdPrefix}-channel-toggle`}
                className="mt-8 flex w-full"
              >
                <ToggleButton
                  active={channel === "phone"}
                  onClick={() => setChannel("phone")}
                  testId={`${testIdPrefix}-channel-phone`}
                >
                  Phone
                </ToggleButton>
                <ToggleButton
                  active={channel === "email"}
                  onClick={() => setChannel("email")}
                  testId={`${testIdPrefix}-channel-email`}
                >
                  Email
                </ToggleButton>
              </div>

              <form onSubmit={submit} className="mt-6">
                <label htmlFor="auth-identifier" className="sr-only">
                  {channel === "phone" ? "Phone number" : "Email address"}
                </label>
                {channel === "phone" ? (
                  <div
                    data-testid={`${testIdPrefix}-phone-wrapper`}
                    className="napoli-phone-wrapper bg-napoli-cream-warm border border-napoli-gold/40 focus-within:border-napoli-oxblood transition-colors"
                  >
                    <PhoneInput
                      international
                      defaultCountry="US"
                      placeholder="Enter phone number"
                      value={identifier}
                      onChange={(value) => setIdentifier(value || "")}
                      data-testid={`${testIdPrefix}-phone-input`}
                      numberInputProps={{
                        id: "auth-identifier",
                        autoComplete: "tel",
                        "aria-label": "Phone number",
                      }}
                    />
                  </div>
                ) : (
                  <input
                    id="auth-identifier"
                    data-testid={`${testIdPrefix}-input`}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-espresso placeholder:text-napoli-espresso-soft/50 px-5 py-4 font-body text-[16px] focus:outline-none focus:border-napoli-oxblood transition-colors"
                    required
                  />
                )}

                <ErrorBanner>{err}</ErrorBanner>

                {mode === "signup" ? (
                  <label
                    htmlFor="auth-promo-optin"
                    data-testid="signup-promo-optin-row"
                    className="mt-4 flex items-start gap-2.5 cursor-pointer"
                  >
                    <input
                      id="auth-promo-optin"
                      data-testid="signup-promo-optin"
                      type="checkbox"
                      checked={promoOptIn}
                      onChange={(e) => setPromoOptIn(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-napoli-oxblood"
                    />
                    <span className="font-body text-[13px] leading-snug text-napoli-espresso-soft">
                      Send me occasional offers and updates from Napoli. (Optional)
                    </span>
                  </label>
                ) : null}

                <button
                  type="submit"
                  data-testid={`${testIdPrefix}-submit`}
                  disabled={submitting || !(identifier || "").trim()}
                  className="mt-4 w-full bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[13px] tracking-widest uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    buttonLabel
                  )}
                </button>
              </form>

              <p
                data-testid={`${testIdPrefix}-footer-link`}
                className="mt-8 font-body text-[14px] text-center text-napoli-espresso-soft"
              >
                {footer.label}{" "}
                <Link
                  to={footer.to}
                  className="text-napoli-oxblood underline underline-offset-4 hover:text-napoli-oxblood-dark"
                >
                  {footer.linkText}
                </Link>
              </p>
              <p className="mt-3 font-body text-[12px] text-center text-napoli-espresso-soft/70">
                {mode === "signup" ? "By joining you agree to our house rules." : "Welcome home."}
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AuthShell;
